import * as L from 'leaflet';
import { Observable } from 'rxjs';

import { MapFeature } from './map-feature';
import { MapObject } from './map-object';
import { MapSelection } from './map-selection';

/** Applies a direct selection hit; returns false when selection is blocked. */
export type MapObjectSelectionHandler = (
  ids: readonly (string | number)[],
  originalEvent?: MouseEvent,
) => boolean;

/** Optional selection capability implemented by an interactive map feature. */
export interface SelectableFeature<
  TObject extends MapObject = MapObject,
> extends MapFeature<TObject> {
  /** Read-only access to the feature-owned selection, using the same IDs as direct hits. */
  readonly selection: Pick<MapSelection<string | number>, 'count' | 'has'> & {
    /** Opaque navigation memento; only this selection owner interprets it. */
    capture?(): unknown;
    restore?(snapshot: unknown): void;
  };
  /** Known business IDs; deferred cluster members need not be expanded yet. */
  readonly selectedObjectIds: readonly (string | number)[];
  /** Emits on every membership change, including replacements with the same count. */
  readonly selectionCount$: Observable<number>;

  /** Read/write resolved business IDs, including members of deferred clusters.
   * Collection writes affect only these objects, never the rest of the selection. */
  isObjectSelected?(id: string | number): boolean;
  setObjectsSelected?(ids: readonly (string | number)[], selected: boolean): void;

  /** Select a business identity supplied by a table, search or navigation. */
  selectObjectById?(id: string | number): void;
  /** Resolve the sole selected object's existing detail presentation. */
  inspectObject?(id: string | number): void;
  /** Resolve a canvas hit without changing selection. */
  objectIdsAt?(latlng: L.LatLng): readonly (string | number)[];

  enableSelection(): void;
  disableSelection(): void;
  clearSelection(): void;

  /** Connect direct marker/shape hits to the shared input controller. */
  setObjectSelectionHandler?(
    handler: MapObjectSelectionHandler | null,
  ): void;
  /** Whether a rendered hit equals this feature's whole selection, including deferred members. */
  matchesObjectSelection?(ids: readonly (string | number)[]): boolean;
  /** Toggle a rendered hit, retaining other selections; partial clusters select all. */
  toggleObjectSelection?(ids: readonly (string | number)[]): void;
  appendObjectSelection?(ids: readonly (string | number)[]): void;

  /** Hit currently rendered objects only; cached/hidden geometry is not a target. */
  selectInBounds?(bounds: L.LatLngBounds): void;
}

/** Narrows a registered map feature to one with selection behaviour. */
export function isSelectableFeature(
  feature: MapFeature | null | undefined,
): feature is SelectableFeature {
  const candidate = feature as Partial<SelectableFeature> | null | undefined;
  return !!candidate
    && typeof candidate.enableSelection === 'function'
    && typeof candidate.disableSelection === 'function'
    && typeof candidate.clearSelection === 'function';
}
